export type AgentAvatarStatus =
  | 'offline'
  | 'idle'
  | 'thinking'
  | 'speaking'
  | 'working'
  | 'awaiting'
  | 'online'
  | 'busy'

export interface AgentAvatarColor {
  bg: string
  fg: string
}

export interface AgentAvatarProps {
  seed?: string
  name?: string
  label?: string
  size?: number | string
  status?: AgentAvatarStatus
  decisionPending?: boolean
  compact?: boolean
  shortLabel?: string
  avatarUrl?: string | null
  imageAlt?: string
  autonomyLevel?: string
  ariaLabel?: string
  /**
   * 严格模式：只渲染真实 avatarUrl 图片，无图片或加载失败时整个组件不渲染。
   * 同时屏蔽 status / autonomyLevel / decisionPending 装饰元素。
   */
  strict?: boolean
}

export interface AgentClusterAgent {
  id: string
  name: string
  avatarSeed?: string
  seed?: string
  status?: AgentAvatarStatus
  task?: string
  currentTask?: string
  decisionPending?: boolean
  avatarUrl?: string | null
  shortLabel?: string
}

export function hashHue(seed: string): number {
  let hash = 0
  for (let i = 0; i < seed.length; i += 1) {
    hash = ((hash << 5) - hash) + seed.charCodeAt(i)
    hash |= 0
  }
  return Math.abs(hash) % 360
}

export function generateAvatarColor(seed: string): AgentAvatarColor {
  const hue = hashHue(seed)
  return {
    bg: `oklch(0.7 0.18 ${hue})`,
    fg: `oklch(0.2 0.05 ${hue})`,
  }
}

export function getInitial(name: string | undefined, fallback = 'A'): string {
  const normalized = (name ?? '').trim()
  if (!normalized) {
    return fallback
  }

  const withoutSuffix = normalized
    .replace(/(专家|助手|智能体|Agent)$/iu, '')
    .trim()
  const withoutPrefix = withoutSuffix.replace(/^(智能|高级|自动)/u, '').trim()
  const source = withoutPrefix || withoutSuffix || normalized
  const first = Array.from(source.replace(/\s+/g, ''))[0]

  if (!first) {
    return fallback
  }
  return /^[a-z]$/i.test(first) ? first.toUpperCase() : first
}

export function toAvatarSize(value: number | string | undefined, fallback: number): string {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return `${Math.max(12, Math.round(value))}px`
  }
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (/^\d+$/.test(trimmed)) {
      return `${trimmed}px`
    }
    if (trimmed.length > 0) {
      return trimmed
    }
  }
  return `${fallback}px`
}
